import React, { Fragment } from 'react'
import { Timeline, Typography } from 'antd'
import { filter, includes, isEmpty, isNil, join, map, omit } from 'lodash'

import { formatDate } from '../../core/utils'

import { EVENT_SUBTYPES } from '../../common/constants'
import { eventDifference, getEventDifferenceText } from '../../common/helpers'
import { formatCreatedByText } from '../../common/utils'

const renderIngredientKeywordEvent = event => {
  const { subtype, original, changes, createdAt, createdBy } = event

  if (subtype === EVENT_SUBTYPES.CREATE_EXPORTED_KEYWORD) {
    return (
      <Timeline.Item key={event.id}>
        Confirmed keyword {changes.term} of suggested type{' '}
        {changes.suggestedType} for {changes.ingredientName}. <br />
        Origin {event.source} from entity id:{' '}
        {changes.externalReference.originEntityId}
        <br />
        {formatDate(createdAt)}
      </Timeline.Item>
    )
  }

  if (subtype === EVENT_SUBTYPES.SENT_KEYWORD_TO_INGREDIENT) {
    return (
      <Timeline.Item key={event.id}>
        Transfered {original.type} {original.term} from{' '}
        {original.ingredientName} to {changes.ingredientName} as {changes.type}
        <br />
        {formatCreatedByText(createdAt, createdBy.name)}
      </Timeline.Item>
    )
  }

  if (subtype === EVENT_SUBTYPES.RECEIVED_KEYWORD_FROM_INGREDIENT) {
    return (
      <Timeline.Item key={event.id}>
        Received on {changes.ingredientName} {changes.type} {changes.term} from{' '}
        {original.ingredientName} as {original.type}
        <br />
        {formatCreatedByText(createdAt, createdBy.name)}
      </Timeline.Item>
    )
  }

  if (subtype === EVENT_SUBTYPES.SENT_KEYWORD_TO_DICTIONARY) {
    return (
      <Timeline.Item key={event.id}>
        Transfered {original.type} {original.term} from{' '}
        {original.ingredientName} to {changes.type} dictionary words
        <br />
        {formatCreatedByText(createdAt, createdBy.name)}
      </Timeline.Item>
    )
  }

  if (subtype === EVENT_SUBTYPES.RECEIVED_KEYWORD_FROM_DICTIONARY) {
    return (
      <Timeline.Item key={event.id}>
        Received {changes.type} {changes.term} on {changes.ingredientName} from{' '}
        {original.type} dictionary words
        <br />
        {formatCreatedByText(createdAt, createdBy.name)}
      </Timeline.Item>
    )
  }

  if (includes(subtype, EVENT_SUBTYPES.DELETE)) {
    return (
      <Timeline.Item key={event.id}>
        Deleted keyword {original.term} of type {original.type} from{' '}
        {original.ingredientName} <br />
        {formatCreatedByText(createdAt, createdBy.name)}
      </Timeline.Item>
    )
  }

  if (includes(subtype, EVENT_SUBTYPES.CREATE)) {
    return (
      <Timeline.Item key={event.id}>
        Created keyword {changes.term} of type {changes.type} for{' '}
        {changes.ingredientName}
        <br />
        {!isNil(createdBy)
          ? formatCreatedByText(createdAt, createdBy.name)
          : `Via confirmation from ${event.source} at ${formatDate(createdAt)}`}
      </Timeline.Item>
    )
  }

  const eventDifferences = eventDifference(
    {
      ...omit(original, [
        'greatGreatGreatGrandParentName',
        'greatGreatGrandParentName',
        'greatGrandParentName',
        'grandParentName',
        'parentName',
        'greatGreatGreatGrandParentType',
        'greatGreatGrandParentType',
        'greatGrandParentType',
        'grandParentType',
        'parentType',
        'greatGreatGreatGrandParentSubtype',
        'greatGreatGrandParentSubtype',
        'greatGrandParentSubtype',
        'grandParentSubtype',
        'parentSubtype',
      ]),
      parent: join(
        filter(
          [
            original.greatGreatGreatGrandParentName,
            original.greatGreatGrandParentName,
            original.greatGrandParentName,
            original.grandParentName,
            original.parentName,
          ],
          item => !isEmpty(item),
        ),
        ' / ',
      ),
      subtype: join(
        filter(
          [
            original.greatGreatGreatGrandParentSubtype,
            original.greatGreatGrandParentSubtype,
            original.greatGrandParentSubtype,
            original.grandParentSubtype,
            original.parentSubtype,
          ],
          item => !isEmpty(item),
        ),
        ' / ',
      ),
    },
    {
      ...omit(changes, [
        'greatGreatGreatGrandParentName',
        'greatGreatGrandParentName',
        'greatGrandParentName',
        'parentName',
        'greatGreatGreatGrandParentType',
        'greatGreatGrandParentType',
        'greatGrandParentType',
        'grandParentType',
        'parentType',
        'greatGreatGreatGrandParentSubtype',
        'greatGreatGrandParentSubtype',
        'greatGrandParentSubtype',
        'grandParentSubtype',
        'parentSubtype',
      ]),
      parent: join(
        filter(
          [
            changes.greatGreatGreatGrandParentName,
            changes.greatGreatGrandParentName,
            changes.greatGrandParentName,
            changes.grandParentName,
            changes.parentName,
          ],
          item => !isEmpty(item),
        ),
        ' / ',
      ),
      subtype: join(
        filter(
          [
            changes.greatGreatGreatGrandParentSubtype,
            changes.greatGreatGrandParentSubtype,
            changes.greatGrandParentSubtype,
            changes.grandParentSubtype,
            changes.parentSubtype,
            changes.subtype,
          ],
          item => !isEmpty(item),
        ),
        ' / ',
      ),
    },
    'term',
    [
      'languageId',
      'parentId',
      'grandParentId',
      'greatGrandParentId',
      'greatGreatGrandParentId',
      'greatGreatGreatGrandParentId',
    ],
  )

  return (
    <Timeline.Item key={event.id}>
      <Typography.Text>
        Updated keyword {original.term} on {original.ingredientName}:
      </Typography.Text>
      <br />
      {map(eventDifferences, ({ differentKey, original, change }) => (
        <Fragment key={differentKey}>
          <Typography.Text>
            {getEventDifferenceText(differentKey, original, change)}
          </Typography.Text>
          <br />
        </Fragment>
      ))}
      <Typography.Text>
        {formatCreatedByText(createdAt, createdBy.name)}
      </Typography.Text>
    </Timeline.Item>
  )
}

export default renderIngredientKeywordEvent
